import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2, User, Mail, CheckCircle2 } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { playNotificationSound } from '@/lib/notifySound';
import { ZertivoSpinner } from '@/components/ZertivoLoader';

const STORE_KEY = 'zv-support-thread';
const STORE_EMAIL = 'zv-support-email';
const STORE_NAME = 'zv-support-name';

function readStore() {
  try { return {
    threadId: localStorage.getItem(STORE_KEY) || null,
    email: localStorage.getItem(STORE_EMAIL) || '',
    name: localStorage.getItem(STORE_NAME) || '',
  }; } catch { return { threadId: null, email: '', name: '' }; }
}
function writeStore(threadId, email, name) {
  try {
    if (threadId) localStorage.setItem(STORE_KEY, threadId); else localStorage.removeItem(STORE_KEY);
    if (email) localStorage.setItem(STORE_EMAIL, email);
    if (name) localStorage.setItem(STORE_NAME, name);
  } catch { /* ignore */ }
}

export default function SupportChatWidget({ hideBubble = false }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [unread, setUnread] = useState(0);
  const scrollRef = useRef(null);
  const lastMsgCountRef = useRef(0);

  // Listen for global "open chat" events so the Navbar's profile dropdown
  // can trigger it even when the floating bubble is hidden on sub-pages.
  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener('zv:open-support-chat', h);
    return () => window.removeEventListener('zv:open-support-chat', h);
  }, []);

  // Init: pull stored thread for anon, or let server attach user thread on first open.
  useEffect(() => {
    const s = readStore();
    if (s.threadId) setThreadId(s.threadId);
    if (s.email && !user) setEmail(s.email);
    if (s.name && !user) setName(s.name);
  }, [user]);

  const loadThread = useCallback(async () => {
    if (!threadId) return;
    setLoading(true); setError('');
    try {
      const params = !user && email ? `?email=${encodeURIComponent(email)}` : '';
      const { data } = await api.get(`/support/threads/${threadId}${params}`);
      const msgs = data.messages || [];
      // Play sound on new admin message arrival when widget is closed.
      const prevCount = lastMsgCountRef.current;
      if (prevCount && msgs.length > prevCount) {
        const newOnes = msgs.slice(prevCount);
        const hasAdmin = newOnes.some(m => m.from === 'admin');
        if (hasAdmin) {
          playNotificationSound();
          if (!open) setUnread(u => u + newOnes.filter(m => m.from === 'admin').length);
        }
      }
      lastMsgCountRef.current = msgs.length;
      setMessages(msgs);
    } catch (e) {
      setError(formatApiError(e) || 'Konnte Chat nicht laden');
      // Clear stale thread id.
      if (e?.response?.status === 404) {
        writeStore(null, email, name); setThreadId(null); setMessages([]);
      }
    } finally { setLoading(false); }
  }, [threadId, user, email, name, open]);

  // Reload on open + polling when open.
  useEffect(() => {
    if (!open || !threadId) return;
    loadThread();
    const t = setInterval(loadThread, 8000);
    return () => clearInterval(t);
  }, [open, threadId, loadThread]);

  // Background polling every 30s when closed but thread exists (detects admin replies).
  useEffect(() => {
    if (open || !threadId) return;
    loadThread();
    const t = setInterval(loadThread, 30000);
    return () => clearInterval(t);
  }, [open, threadId, loadThread]);

  // Auto-scroll on new message.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  // Clear unread count when user opens the widget.
  useEffect(() => { if (open) setUnread(0); }, [open]);

  const send = async (e) => {
    e?.preventDefault?.();
    const msg = input.trim();
    if (!msg) return;
    setError('');
    if (!user && (!name.trim() || !/\S+@\S+\.\S+/.test(email))) {
      setError('Bitte Name und E-Mail eingeben.');
      return;
    }
    setSending(true);
    try {
      if (!threadId) {
        const { data } = await api.post('/support/threads', {
          name: user ? (user.name || '') : name,
          email: user ? (user.email || '') : email,
          message: msg,
        });
        setThreadId(data.thread_id);
        writeStore(data.thread_id, email, name);
        setInput('');
        setTimeout(loadThread, 200);
      } else {
        const params = !user && email ? `?email=${encodeURIComponent(email)}` : '';
        await api.post(`/support/threads/${threadId}/reply${params}`, { message: msg });
        setInput('');
        setTimeout(loadThread, 200);
      }
    } catch (err) {
      setError(formatApiError(err) || 'Senden fehlgeschlagen');
    } finally { setSending(false); }
  };

  const resetChat = () => {
    writeStore(null, email, name);
    setThreadId(null); setMessages([]); lastMsgCountRef.current = 0;
  };

  return (
    <>
      {/* Floating Bubble — auf Wunsch des Produkts komplett ausgeblendet.
          Chat bleibt via Footer-Link / Kontakt-Seite / Profil-Dropdown
          (Event `zv:open-support-chat`) erreichbar. */}
      {false && !hideBubble && (
        <button
          aria-label="Support-Chat öffnen"
          onClick={() => setOpen(o => !o)}
          data-testid="support-chat-toggle"
          style={{ bottom: 'calc(1.25rem + var(--mobile-nav-offset, 0px))' }}
          className="fixed z-[90] right-5 h-14 w-14 rounded-full bg-gradient-to-br from-[#00D387] to-[#0F8A5F] text-white shadow-[0_14px_40px_-8px_rgba(0,211,135,0.55)] flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
        >
          {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
          {!open && unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#DC2626] text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-white">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      )}

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 260, damping: 26 }}
            style={{ bottom: 'calc(6rem + var(--mobile-nav-offset, 0px))' }}
            className="fixed z-[91] right-5 w-[360px] max-w-[calc(100vw-2rem)] h-[540px] max-h-[75vh] rounded-3xl bg-white border border-[#E4E4E7] shadow-[0_24px_60px_-10px_rgba(9,9,11,0.2)] flex flex-col overflow-hidden"
            data-testid="support-chat-panel"
          >
            {/* Header */}
            <div className="p-4 bg-gradient-to-br from-[#00D387] via-[#0F8A5F] to-[#065F43] text-white flex items-center justify-between">
              <div>
                <p className="font-heading font-bold text-[15px]">Zertivo Support</p>
                <p className="text-[11.5px] opacity-90">Direkter Draht zu unserem Team.</p>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Schließen" className="w-9 h-9 rounded-full hover:bg-white/15 flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAFAFA]">
              {!threadId && messages.length === 0 && !loading && (
                <div className="text-center py-6">
                  <MessageCircle className="w-10 h-10 mx-auto text-[#00D387] opacity-50 mb-2" />
                  <p className="text-[13px] text-[#52525B]">Womit können wir Ihnen helfen?</p>
                  <p className="text-[11.5px] text-[#71717A] mt-1">Schreiben Sie uns direkt — echter Mensch, kein Bot.</p>
                </div>
              )}
              {loading && !messages.length && (
                <div className="flex justify-center py-6"><ZertivoSpinner size={20} /></div>
              )}
              {messages.map((m) => (
                <Bubble key={m.id} msg={m} />
              ))}
            </div>

            {/* Form */}
            <form onSubmit={send} className="p-3 border-t border-[#E4E4E7] bg-white space-y-2">
              {!user && !threadId && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A1A1AA]" />
                    <input type="text" value={name} onChange={e => setName(e.target.value)}
                      data-testid="support-name" placeholder="Name"
                      className="w-full h-9 pl-9 pr-3 bg-[#F9FAFB] border border-[#E4E4E7] rounded-lg text-[12.5px] focus:border-[#00D387] outline-none" />
                  </div>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A1A1AA]" />
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                      data-testid="support-email" placeholder="E-Mail"
                      className="w-full h-9 pl-9 pr-3 bg-[#F9FAFB] border border-[#E4E4E7] rounded-lg text-[12.5px] focus:border-[#00D387] outline-none" />
                  </div>
                </div>
              )}
              {error && <p className="text-[11.5px] text-[#DC2626]" data-testid="support-error">{error}</p>}
              <div className="flex items-center gap-2">
                <input type="text" value={input} onChange={e => setInput(e.target.value)}
                  placeholder="Nachricht eingeben…"
                  data-testid="support-message"
                  className="flex-1 h-10 px-3 bg-[#F9FAFB] border border-[#E4E4E7] rounded-xl text-[13px] focus:border-[#00D387] focus:ring-2 focus:ring-[#00D387]/15 outline-none" />
                <button type="submit" disabled={sending || !input.trim()}
                  data-testid="support-send-btn"
                  className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#00D387] to-[#0F8A5F] text-white disabled:opacity-40 flex items-center justify-center">
                  {sending ? <ZertivoSpinner size={16} /> : <Send className="w-4 h-4" />}
                </button>
              </div>
              {threadId && (
                <button type="button" onClick={resetChat}
                  className="text-[11px] text-[#A1A1AA] hover:text-[#52525B] transition-colors">
                  Neue Unterhaltung starten
                </button>
              )}
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Bubble({ msg }) {
  const isAdmin = msg.from === 'admin';
  return (
    <div className={`flex ${isAdmin ? 'justify-start' : 'justify-end'}`}>
      <div className={`max-w-[78%] px-3.5 py-2 rounded-2xl text-[13px] leading-relaxed ${
        isAdmin
          ? 'bg-white border border-[#E4E4E7] text-[#09090B] rounded-tl-sm'
          : 'bg-gradient-to-br from-[#00D387] to-[#0F8A5F] text-white rounded-tr-sm'
      }`}>
        {isAdmin && (
          <p className="text-[10px] font-bold text-[#0F8A5F] mb-1 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Zertivo Support
          </p>
        )}
        <p className="whitespace-pre-wrap">{msg.message}</p>
      </div>
    </div>
  );
}
